// Role-context helpers. A role context is a string the API issues and
// validates: 'parent', 'kid', 'super_admin', 'content_editor', 'support',
// 'teacher:<schoolId>', 'school_admin:<schoolId>', 'student:<schoolId>', …
// The consumer app only renders shells for parent / kid / teacher /
// school_admin; staff roles live in the admin app, so they fall through.

export type ShellKind = 'parent' | 'kid' | 'teacher' | 'school_admin';

/** The bare role name without any `:scope` suffix. */
export function roleKind(context: string): string {
  const colon = context.indexOf(':');
  return colon === -1 ? context : context.slice(0, colon);
}

/** The school id from a scoped context, or null for unscoped roles. */
export function roleScope(context: string): string | null {
  const colon = context.indexOf(':');
  return colon === -1 ? null : context.slice(colon + 1);
}

/** Which shell a role context maps to, or null if the app has no shell for it. */
export function shellForContext(context: string | null): ShellKind | null {
  if (!context) return null;
  switch (roleKind(context)) {
    case 'parent':
      return 'parent';
    case 'kid':
    case 'student':
      return 'kid';
    case 'teacher':
      return 'teacher';
    case 'school_admin':
      return 'school_admin';
    default:
      return null;
  }
}

/** Role contexts that the consumer app can actually render a shell for. */
export function appRoleContexts(available: string[]): string[] {
  return available.filter((c) => shellForContext(c) !== null);
}

/** i18n key for a role context's human label, used in the role switcher. */
export function roleLabelKey(context: string): string {
  const kind = shellForContext(context);
  switch (kind) {
    case 'parent':
      return 'app.common.role_parent';
    case 'kid':
      return 'app.common.role_kid';
    case 'teacher':
      return 'app.common.role_teacher';
    case 'school_admin':
      return 'app.common.role_school_admin';
    default:
      return 'app.common.role_parent';
  }
}
